import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons/faQuestionCircle';

import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { defineMessages, FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import { Tooltip } from 'ui-components/components/tooltip';

import Global from '../../../../store/global-selectors';
import { USD } from '../../../../constants/currency-codes';
import { calculateTotalAmount } from '../../../../fulfillment-plans/amount-summary/helper';
import { CountryConfigNames } from '../../../../contexts/country-config-context/country-config-enums';
import { useCountryConfig } from '../../../../hooks/use-country-config';
import CurrencyComponent from './currency-component';
import Discounts from './discounts';

import style from './purchase-details.module.less';
import { emptyArray } from 'ui-components/default-props/empty';

const messages = defineMessages({
  sectionTitle: {
    description: 'order details section title',
    defaultMessage: 'Cost',
    id: 'GwTyas',
  },
  productTotal: {
    description: 'orderDetails.productTotal',
    defaultMessage: 'Product',
    id: '/D/EXx',
  },
  shippingPaid: {
    description: 'orderDetails.shippingPaid',
    defaultMessage: 'Shipping',
    id: '0SNSdr',
  },
  taxPaid: {
    description: 'orderDetails.taxPaid',
    defaultMessage: 'Tax',
    id: '7c5R+W',
  },
  totalOrder: {
    description: 'orderDetails.totalOrder',
    defaultMessage: 'Total',
    id: 'KIIKge',
  },
  totalPaid: {
    description: 'orderDetails.totalPaid',
    defaultMessage: 'Total Paid',
    id: 'Nkr5SG',
  },
  totalPaidTooltip: {
    description: 'order details totalPaid tooltip',
    defaultMessage:
      'Total Paid as reported by the marketplace. This may include cost adjustments, such as discounts, taxes, shipping, etc.',
    id: '99+yVT',
  },
});

const TotalPaidMessage = () => {
  return (
    <div className={style['total-paid-detail-line']}>
      <FormattedMessage {...messages.totalPaid} />
      <Tooltip position='bottom' text={<FormattedMessage {...messages.totalPaidTooltip} />}>
        <div>
          <FontAwesomeIcon className={style.icon} icon={faQuestionCircle} />
        </div>
      </Tooltip>
    </div>
  );
};

const PurchaseDetails = ({ amountSummary = null, discounts = emptyArray }) => {
  const [isTaxesIncludedInOrdersTotal] = useCountryConfig(CountryConfigNames.TaxesIncludedInOrdersTotal);
  const sellerCurrencyCode = useSelector(Global.selectSellerDefaultCurrency) || USD;
  const defaultAmount = useMemo(() => {
    return { value: 0, code: sellerCurrencyCode };
  }, [sellerCurrencyCode]);

  if (!amountSummary) {
    return null;
  }

  const { productTotal, orderTotal, shippingPaid, taxPaid, totalPaid } = amountSummary;
  const tempShippingPaid = shippingPaid || defaultAmount;
  const tempTaxPaid = taxPaid || defaultAmount;
  const tempProductTotal = productTotal || defaultAmount;
  const tempTotalPaid = totalPaid || defaultAmount;

  const orderTotalValue = calculateTotalAmount(
    tempProductTotal.value,
    tempShippingPaid.value,
    tempTaxPaid.value,
    isTaxesIncludedInOrdersTotal,
  );
  const tempOrderTotal = orderTotal ? { ...orderTotal, value: orderTotalValue } : defaultAmount;
  return (
    <div data-testid='order-summary-purchase-details'>
      <div className={style['section-title']}>
        <FormattedMessage {...messages.sectionTitle} />
      </div>
      <CurrencyComponent
        currencyCode={tempProductTotal.code}
        dataTestId='product-total'
        label={<FormattedMessage {...messages.productTotal} />}
        value={tempProductTotal.value}
      />
      <Discounts discounts={discounts} />
      <CurrencyComponent
        currencyCode={tempShippingPaid.code}
        dataTestId='shipping-paid'
        label={<FormattedMessage {...messages.shippingPaid} />}
        value={tempShippingPaid.value}
      />
      <CurrencyComponent
        currencyCode={tempTaxPaid.code}
        dataTestId='tax-paid'
        label={<FormattedMessage {...messages.taxPaid} />}
        value={tempTaxPaid.value}
      />
      <CurrencyComponent
        currencyCode={tempOrderTotal.code}
        dataTestId='total-order'
        label={<FormattedMessage {...messages.totalOrder} />}
        value={tempOrderTotal.value}
      />
      <CurrencyComponent
        currencyCode={tempTotalPaid.code}
        dataTestId='total-paid'
        label={<TotalPaidMessage />}
        value={tempTotalPaid.value}
      />
    </div>
  );
};

PurchaseDetails.propTypes = {
  amountSummary: PropTypes.object,
  discounts: PropTypes.array,
};

export default PurchaseDetails;
